import React, { useMemo } from "react";
import { observer } from "mobx-react-lite";
import { Hooks } from "@xt/xtbase";
import step7Img from "@/assets/img/cfd-guide/guide1.png";
import lineImg from "@/assets/img/cfd-guide/line.png";
import store from "store";
import { useCfdGuideStageRect } from "./useCfdGuideStageRect";
import { useCfdGuideAnchoredTipLayout } from "./useCfdGuideAnchoredTipLayout";
import styles from "./ProGuideStep7.module.scss";

const { useTranslation } = Hooks;

export type ProGuideStep7Props = {
  onSkip: () => void;
  onNext: () => void;
};

const DESIGN_WIDTH = 375;
/** 375 设计稿下完整「信息行 + 双按钮」示意最低高度，未登录时真实容器仅登录条，用此保证高亮内仍能展示下单 UI */
const PRO_STEP7_OVERLAY_MIN_HEIGHT_DESIGN = 220;

function getScale(): number {
  if (typeof window === "undefined") return 1;
  const w = window.innerWidth || DESIGN_WIDTH;
  return w / DESIGN_WIDTH;
}

const PRO_ACTION_ANCHOR_SELECTOR = '#cfd-trade-form-pro [data-guide-target="proActionButtons"]';

const ProGuideStep7: React.FC<ProGuideStep7Props> = ({ onSkip, onNext }) => {
  const t = useTranslation();
  const { name } = store.market;

  const scale = getScale();
  const anchor = useCfdGuideStageRect(PRO_ACTION_ANCHOR_SELECTOR);

  const overlayHeight =
    anchor != null
      ? Math.max(anchor.height, PRO_STEP7_OVERLAY_MIN_HEIGHT_DESIGN * scale)
      : 0;

  const tipAnchorForLayout = useMemo(
    () =>
      anchor
        ? { top: anchor.top, left: anchor.left, width: anchor.width, height: overlayHeight }
        : null,
    [anchor, overlayHeight]
  );

  const tipLayout = useCfdGuideAnchoredTipLayout(tipAnchorForLayout, scale);

  const coinPriceUpperCase = useMemo(() => {
    if (!name) return "USDT";
    const parts = name.split("_");
    return parts[1]?.toUpperCase() || "USDT";
  }, [name]);

  const maxOpenBuy = "1000";
  const costBuy = "100";
  const maxOpenSell = "1000";
  const costSell = "100";

  const imgSrc = (step7Img as any)?.src || (step7Img as any);
  const lineImgSrc = (lineImg as any)?.src || (lineImg as any);

  return (
    <div style={{ ["--sg-scale" as any]: scale }}>
      {anchor && tipLayout ? (
        <>
          <div
            className={styles.highlightRect}
            style={{
              position: "fixed",
              left: anchor.left,
              top: anchor.top,
              width: anchor.width,
              height: overlayHeight,
              zIndex: 10001,
            }}
          >
            <div className={styles.actionButtons}>
              <div className={styles.actionButtonGroup}>
                <div className={styles.actionInfoRow}>
                  <div className={styles.actionInfoItem}>
                    <span className={styles.actionInfoLabel}>{t("cfd.maxOpen")}</span>
                    <span className={styles.actionInfoValue}>
                      {maxOpenBuy} {coinPriceUpperCase}
                    </span>
                  </div>
                  <div className={styles.actionInfoItem}>
                    <span className={styles.actionInfoLabel}>{t("cfd.cost")}</span>
                    <span className={styles.actionInfoValue}>
                      {costBuy} {coinPriceUpperCase}
                    </span>
                  </div>
                </div>
                <button type="button" className={`${styles.actionBtn} ${styles.buyBtn}`}>
                  {t("cfd.buyUp")}
                </button>
              </div>
              <div className={styles.actionButtonGroup}>
                <div className={styles.actionInfoRow}>
                  <div className={styles.actionInfoItem}>
                    <span className={styles.actionInfoLabel}>{t("cfd.maxOpen")}</span>
                    <span className={styles.actionInfoValue}>
                      {maxOpenSell} {coinPriceUpperCase}
                    </span>
                  </div>
                  <div className={styles.actionInfoItem}>
                    <span className={styles.actionInfoLabel}>{t("cfd.cost")}</span>
                    <span className={styles.actionInfoValue}>
                      {costSell} {coinPriceUpperCase}
                    </span>
                  </div>
                </div>
                <button type="button" className={`${styles.actionBtn} ${styles.sellBtn}`}>
                  {t("cfd.buyDown")}
                </button>
              </div>
            </div>
          </div>

          <div
            className={styles.tip}
            style={{
              position: "fixed",
              left: tipLayout.tipLeft,
              top: anchor.top + overlayHeight + tipLayout.gapBelow,
              width: tipLayout.tipWidth,
              zIndex: 10001,
            }}
          >
            <div className={styles.tipBg}>
              <div className={styles.tipText}>
                <div className={styles.tipTitle}>{t("cfd.guide.pro.step7.title")}</div>
                <div className={styles.tipDesc}>{t("cfd.guide.pro.step7.desc")}</div>
              </div>
              <img className={styles.tipImg} src={imgSrc} alt="" />
            </div>
            <div className={styles.star1} />
            <div className={styles.star2} />
            <img
              className={styles.lineImg}
              src={lineImgSrc}
              alt=""
              style={{
                left: tipLayout.lineCenterInTip,
                transform: "translateX(-50%)",
              }}
            />
          </div>
        </>
      ) : null}

      <div className={styles.btnGroup}>
        <button type="button" className={`${styles.btn} ${styles.btnPrimary}`} onClick={onNext}>
          {`${t("cfd.guide.next")} 7/9`}
        </button>
        <button type="button" className={`${styles.btn} ${styles.btnGhost}`} onClick={onSkip}>
          {t("cfd.guide.skip")}
        </button>
      </div>
    </div>
  );
};

export default observer(ProGuideStep7);
